import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  LinearProgress
} from "@mui/material";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import UserQuery from "../../stateQueries/User";
// import Table from "@mui/material/Table";

// import TableCell from "@mui/material/TableCell";
// import TableContainer from "@mui/material/TableContainer";
// import TableHead from "@mui/material/TableHead";
// import TableRow from "@mui/material/TableRow";
// import Paper from "@mui/material/Paper";

function LifeLongLearningEvents() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { data, isLoading } = useQuery({
    queryKey: ["llEvents"],
    queryFn: async () => {
      return UserQuery.getAllLLEvents();
    }
  });

  if (isLoading) {
    return <LinearProgress />;
  }

  const trainingData = data?.llEvents;

  if (!trainingData || trainingData.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography
          fontSize={20}
          fontWeight="bolder"
          sx={{ color: "primary.main" }}
        >
          LIFELONG LEARNING WORKSHOPS
        </Typography>
        <Divider />
        <Alert severity="info">
          No lifelong learning events available at the moment. Please check back
          later.
        </Alert>
      </Stack>
    );
  }

  // Mobile Card Component for better mobile experience
  const MobileTrainingCard = ({
    index,
    provider,
    topic,
    trainingDates,
    registrationLink
  }) => (
    <Card
      sx={{
        mb: 2,
        boxShadow: 3,
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
          transition: "all 0.3s ease-in-out"
        }
      }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Chip
            label={`#${index + 1}`}
            color="primary"
            size="small"
            sx={{ fontWeight: "bold" }}
          />
        </Box>

        <Typography
          variant="h6"
          color="primary"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          {provider}
        </Typography>

        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          {topic}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {trainingDates?.map((td) => `📅 ${td.date} ${td.time}`).join(" | ")}
        </Typography>

        <Button
          variant="contained"
          href={registrationLink}
          target="_blank"
          rel="noreferrer"
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: "bold"
          }}
        >
          Register Now
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Stack spacing={2}>
      <Typography
        fontSize={20}
        fontWeight="bolder"
        sx={{ color: "primary.main" }}
      >
        LIFELONG LEARNING WORKSHOPS
      </Typography>
      <Divider />
      <Typography fontWeight="bolder">Dear Stakeholders</Typography>
      <Typography>
        FASSET has once again commenced with the Lifelong Learning online
        training that is facilitated by partner skills Professional Bodies. The
        trainings are currently running from September 2024 till March 2025 and
        topics have been formulated to assist sector employees to gain
        competitive edge through fine tuning of skills required in the
        workplace. Register for a date and time convenient for you for any of
        the topics below. We look forward to hosting you.
      </Typography>
      {isMobile ? (
        // Mobile View - Card Layout
        <Box>
          {trainingData?.map((training, index) => (
            <MobileTrainingCard
              key={training.id}
              index={index}
              provider={training.serviceProvider}
              topic={training.topic}
              trainingDates={training.trainingDates}
              registrationLink={training.registrationLink}
            />
          ))}
        </Box>
      ) : (
        // Desktop View - Enhanced Table
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: 4,
            overflow: "hidden",
            "& .MuiTable-root": {
              minWidth: 650
            }
          }}
        >
          <Table aria-label="lifelong learning events table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    backgroundColor: "primary.main",
                    fontSize: "0.9rem",
                    py: 2
                  }}
                >
                  No#
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    backgroundColor: "primary.main",
                    fontSize: "0.9rem",
                    py: 2,
                    minWidth: 200
                  }}
                >
                  Service Provider
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    backgroundColor: "primary.main",
                    fontSize: "0.9rem",
                    py: 2,
                    minWidth: 250
                  }}
                >
                  Topic
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    backgroundColor: "primary.main",
                    fontSize: "0.9rem",
                    py: 2,
                    minWidth: 200
                  }}
                >
                  Date and Times of Training
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    color: "#FFFFFF",
                    backgroundColor: "primary.main",
                    fontSize: "0.9rem",
                    py: 2
                  }}
                >
                  Registration Link
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trainingData?.map((training, trainingIndex) =>
                training.trainingDates.map((dateItem, dateIndex) => (
                  <TableRow
                    key={`${training.id}-${dateIndex}`}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "rgba(0, 0, 0, 0.02)"
                      },
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                        transition: "background-color 0.3s ease"
                      }
                    }}
                  >
                    {dateIndex === 0 && (
                      <>
                        <TableCell
                          align="center"
                          component="th"
                          scope="row"
                          rowSpan={training.trainingDates.length}
                          sx={{
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                            color: "primary.main",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            py: 3
                          }}
                        >
                          {trainingIndex + 1}
                        </TableCell>
                        <TableCell
                          align="center"
                          component="th"
                          scope="row"
                          rowSpan={training.trainingDates.length}
                          sx={{
                            fontWeight: "bold",
                            color: "text.primary",
                            borderRight: "1px solid rgba(224, 224, 224, 1)",
                            py: 3,
                            fontSize: "0.9rem"
                          }}
                        >
                          {training.serviceProvider}
                        </TableCell>
                      </>
                    )}
                    {dateIndex === 0 && (
                      <TableCell
                        align="center"
                        component="th"
                        scope="row"
                        rowSpan={training.trainingDates.length}
                        sx={{
                          fontWeight: 500,
                          py: 2,
                          borderRight: "1px solid rgba(224, 224, 224, 1)"
                        }}
                      >
                        {training.topic}
                      </TableCell>
                    )}
                    <TableCell
                      align="center"
                      scope="row"
                      sx={{
                        py: 2,
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        fontFamily: "monospace",
                        fontSize: "0.85rem"
                      }}
                    >
                      {dateItem.date} ({dateItem.time})
                    </TableCell>
                    {dateIndex === 0 && (
                      <TableCell
                        align="center"
                        scope="row"
                        rowSpan={training.trainingDates.length}
                        sx={{ py: 3 }}
                      >
                        <Button
                          variant="contained"
                          href={training.registrationLink}
                          target="_blank"
                          rel="noreferrer"
                          size="small"
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: "bold",
                            px: 3
                          }}
                        >
                          Register
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <br />
      <Typography>
        For any further enquiries please contact{" "}
        <a href={`mailto:lifelonglearning@fasset.org.za`}>
          lifelonglearning@fasset.org.za
        </a>{" "}
        | +27 011 476 8570
      </Typography>
    </Stack>
  );
}

export default LifeLongLearningEvents;
